import { FlightSummary } from '@models/micro/flight-summary.model';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import {
  SSBD_CABALLERO_AGUILA_FF_TIER_LEVELS_BY_PROGRAM,
  SSBD_LABEL_CONNECTION,
  SSBD_LABEL_PRIORITY,
  SSBD_LABEL_PRIORITY_CABALLERO_AGUILA,
  SSBD_LABEL_PRIORITY_CONNECTION,
  SSBD_LABEL_STANDBY,
  SSBD_PRIORITY_FF_TIER_LEVELS_BY_PROGRAM,
} from '@app/constants/ssbd-printer.constant';

/** Tarifas Priority: cualquier `fareFamily` que incluya "PREMIER" o "AMPLUS". */
const PRIORITY_FARE_FAMILY_PREMIER = 'PREMIER';
const PRIORITY_FARE_FAMILY_AMPLUS = 'AMPLUS';

/**
 * Escenario que determina qué label SSBD imprimir. `null` = ningún label.
 */
export type SsbdBagtagScenario =
  | 'STANDBY'
  | 'PRIORITY_CONNECTION'
  | 'CONNECTION'
  | 'PRIORITY_CABALLERO_AGUILA'
  | 'PRIORITY'
  | null;

/**
 * La reservación está en standby (lista de espera).
 */
export function isStandbyReservation(reservation: Reservation): boolean {
  return !!(reservation?.isStandBy || reservation?.isStandByBooking);
}

/**
 * `ffTierLevel` puede venir compuesto por más de un código separado por "/"
 * (ej. "TTP/ELP"), donde cada segmento es un nivel/beneficio distinto del
 * mismo viajero frecuente. Se descompone en códigos individuales, sin
 * espacios y en mayúsculas, para poder validarlos contra los catálogos
 * (SSBD_PRIORITY_FF_TIER_LEVELS_BY_PROGRAM / SSBD_CABALLERO_AGUILA_FF_TIER_LEVELS_BY_PROGRAM)
 * sin depender de que el string completo coincida exactamente.
 */
function splitFfTierLevelCodes(ffTierLevel: string | null | undefined): string[] {
  if (!ffTierLevel) {
    return [];
  }
  return ffTierLevel
    .split('/')
    .map((code) => code?.trim().toUpperCase())
    .filter((code) => !!code);
}

/** Alguno de los códigos de `ffTierLevel` (ver splitFfTierLevelCodes) está en `validTiers`. */
function matchesAnyTierCode(
  ffTierLevel: string | null | undefined,
  validTiers?: string[]
): boolean {
  if (!validTiers?.length) {
    return false;
  }
  const codes = splitFfTierLevelCodes(ffTierLevel);
  return codes.some((code) => validTiers.includes(code));
}

/**
 * El tramo actual no es el último del itinerario: al pasajero le queda al
 * menos un vuelo más, por lo que su maleta conecta.
 */
export function isPassengerInConnection(flightSummary: FlightSummary): boolean {
  const segments = flightSummary?.segments;
  if (!segments || segments.length < 2) {
    return false;
  }

  const currentSegment = flightSummary?.currentSegment;
  if (!currentSegment) {
    return false;
  }

  const lastSegment = segments[segments.length - 1];
  return currentSegment.segmentCode !== lastSegment.segmentCode;
}

/**
 * El nivel de viajero frecuente del pasajero (ffProgram + ffTierLevel)
 * califica como Priority genérico (ver SSBD_PRIORITY_FF_TIER_LEVELS_BY_PROGRAM).
 * Caballero Águila no entra aquí: usa isCaballeroAguilaByLoyaltyTier.
 */
export function isSkyPriorityByLoyaltyTier(passenger: Passenger): boolean {
  const { ffNumber, ffProgram, ffTierLevel } = passenger?.passengerInfo || {};

  if (!ffNumber || !ffProgram || !ffTierLevel) {
    return false;
  }

  const validTiers = SSBD_PRIORITY_FF_TIER_LEVELS_BY_PROGRAM[ffProgram];
  return matchesAnyTierCode(ffTierLevel, validTiers);
}

/**
 * El nivel de viajero frecuente del pasajero califica como Caballero Águila
 * (ver SSBD_CABALLERO_AGUILA_FF_TIER_LEVELS_BY_PROGRAM).
 */
export function isCaballeroAguilaByLoyaltyTier(passenger: Passenger): boolean {
  const { ffNumber, ffProgram, ffTierLevel } = passenger?.passengerInfo || {};

  if (!ffNumber || !ffProgram || !ffTierLevel) {
    return false;
  }

  const validTiers = SSBD_CABALLERO_AGUILA_FF_TIER_LEVELS_BY_PROGRAM[ffProgram];
  return matchesAnyTierCode(ffTierLevel, validTiers);
}

/** La tarifa del itinerario es una variante Premier (PREMIER, PREMIER_BASIC, etc). */
export function isPremierFare(flightSummary: FlightSummary): boolean {
  const fareFamily = flightSummary?.fareFamily as unknown as string;
  return !!fareFamily?.toUpperCase().includes(PRIORITY_FARE_FAMILY_PREMIER);
}

/** La tarifa del itinerario es una variante AmPlus (AMPLUS, AMPLUS_BASIC, etc). */
export function isAmPlusFare(flightSummary: FlightSummary): boolean {
  const fareFamily = flightSummary?.fareFamily as unknown as string;
  return !!fareFamily?.toUpperCase().includes(PRIORITY_FARE_FAMILY_AMPLUS);
}

/**
 * El pasajero es SkyPriority: bandera de backend, nivel de viajero
 * frecuente (isSkyPriorityByLoyaltyTier) o tarifa Premier/AmPlus del itinerario.
 */
export function resolveIsSkyPriority(passenger: Passenger, flightSummary: FlightSummary): boolean {
  if (passenger?.isSkyPriority) {
    return true;
  }
  if (isSkyPriorityByLoyaltyTier(passenger)) {
    return true;
  }

  return isPremierFare(flightSummary) || isAmPlusFare(flightSummary);
}

/**
 * Alguno de los pasajeros seleccionados para el bagtag es SkyPriority,
 * resuelto vía resolveIsSkyPriority() (bandera del backend, programa/nivel de
 * viajero frecuente o tarifa Premier del itinerario).
 */
export function isPriorityPassenger(
  passengers: Passenger[],
  flightSummary?: FlightSummary
): boolean {
  return !!passengers?.some((passenger) => resolveIsSkyPriority(passenger, flightSummary));
}

/** Alguno de los pasajeros es Caballero Águila (ver isCaballeroAguilaByLoyaltyTier). */
export function isCaballeroAguilaPassenger(passengers: Passenger[]): boolean {
  return !!passengers?.some((passenger) => isCaballeroAguilaByLoyaltyTier(passenger));
}

/**
 * La maleta conecta y además el pasajero es Priority o Caballero Águila:
 * ambos casos comparten el label PriorityConectionSSBD1.
 */
export function isPriorityInConnection(reservation: Reservation, passengers: Passenger[]): boolean {
  const flightSummary = reservation?.flightSummary;
  if (!isPassengerInConnection(flightSummary)) {
    return false;
  }
  return isPriorityPassenger(passengers, flightSummary) || isCaballeroAguilaPassenger(passengers);
}

/**
 * Resuelve el escenario del bagtag SSBD. Orden de precedencia: STANDBY >
 * PRIORITY_CONNECTION > CONNECTION > PRIORITY_CABALLERO_AGUILA > PRIORITY.
 *
 * PENDIENTE (CI-22791): escenario de conexión corta (SSBD_LABEL_SHORT_CONNECTION)
 * sin condición de negocio confirmada; no se contempla todavía.
 */
export function resolveSsbdBagtagScenario(
  reservation: Reservation,
  passengers: Passenger[]
): SsbdBagtagScenario {
  if (isStandbyReservation(reservation)) {
    return 'STANDBY';
  }
  if (isPriorityInConnection(reservation, passengers)) {
    return 'PRIORITY_CONNECTION';
  }
  if (isPassengerInConnection(reservation?.flightSummary)) {
    return 'CONNECTION';
  }
  if (isCaballeroAguilaPassenger(passengers)) {
    return 'PRIORITY_CABALLERO_AGUILA';
  }
  if (isPriorityPassenger(passengers, reservation?.flightSummary)) {
    return 'PRIORITY';
  }
  return null;
}

/** Nombre del label SSBD a imprimir según resolveSsbdBagtagScenario(). `null` = no imprimir. */
export function resolveSsbdLabelName(
  reservation: Reservation,
  passengers: Passenger[]
): string | null {
  const scenario = resolveSsbdBagtagScenario(reservation, passengers);

  switch (scenario) {
    case 'STANDBY':
      return SSBD_LABEL_STANDBY;
    case 'PRIORITY_CONNECTION':
      return SSBD_LABEL_PRIORITY_CONNECTION;
    case 'CONNECTION':
      return SSBD_LABEL_CONNECTION;
    case 'PRIORITY_CABALLERO_AGUILA':
      return SSBD_LABEL_PRIORITY_CABALLERO_AGUILA;
    case 'PRIORITY':
      return SSBD_LABEL_PRIORITY;
    default:
      return null;
  }
}
